import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Folder, MoveVertical } from "lucide-react";
import { Category } from "@/types/admin";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface CategoryTreeProps {
  categories: (Category & { children?: Category[] })[];
  selectedCategory: number | null;
  onSelectCategory: (categoryId: number) => void;
  expandedCategories: number[];
  onToggleExpand: (categoryId: number) => void;
  onMoveCategory?: (categoryId: number, targetId: number | null) => void;
  level?: number;
  allowMoving?: boolean;
}

export function CategoryTree({
  categories,
  selectedCategory,
  onSelectCategory,
  expandedCategories,
  onToggleExpand,
  onMoveCategory,
  level = 0,
  allowMoving = false
}: CategoryTreeProps) {
  return (
    <div className={`${level > 0 ? 'ml-4 pl-2 border-l border-border' : ''}`}>
      {categories.map((category) => {
        const hasChildren = category.children && category.children.length > 0;
        const isExpanded = expandedCategories.includes(category.id);
        const isSelected = selectedCategory === category.id;

        return (
          <div key={category.id} className="my-1">
            <div
              className={`flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 cursor-pointer ${
                isSelected ? 'bg-muted/70 font-medium' : ''
              }`}
            >
              {hasChildren ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand(category.id);
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              ) : (
                <div className="w-5" />
              )}
              <div
                className="flex-1 flex items-center gap-2"
                onClick={() => onSelectCategory(category.id)}
              >
                <Folder className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">{category.name}</span>
              </div>

              {allowMoving && onMoveCategory && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoveVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => onMoveCategory(category.id, null)}>
                      Mover a raíz
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {categories
                      .filter(c => c.id !== category.id)
                      .map(targetCategory => (
                        <DropdownMenuItem
                          key={targetCategory.id}
                          onClick={() => onMoveCategory(category.id, targetCategory.id)}
                        >
                          Mover a {targetCategory.name}
                        </DropdownMenuItem>
                      ))
                    }
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            {hasChildren && isExpanded && (
              <CategoryTree
                categories={category.children || []}
                selectedCategory={selectedCategory}
                onSelectCategory={onSelectCategory}
                expandedCategories={expandedCategories}
                onToggleExpand={onToggleExpand}
                onMoveCategory={onMoveCategory}
                level={level + 1}
                allowMoving={allowMoving}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
